import React, { createContext, useEffect, useState } from "react";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../firebase-config";
import { getArgentinaFormat } from "../utils/utils";
import {
  BUSINESS_COLLECTION,
  SHIFT_COLLECTION,
  USERS_COLLECTION,
} from "../utils/constants";
import { IBusiness, IShift, IUser } from "../interfaces";

export const UserContext = createContext(
  {} as {
    userData: IUser;
    businessData: IBusiness;
    loading: boolean;
    isAuthenticated: boolean;
    shiftInfo: IShift | null;
    handleSetUser(id: string): void;
    handleSignOut(): void;
    setLoading(loading: boolean): void;
    updateShift(
      action: string,
      updatedData?: { totalAmount: number; totalOrders: number }
    ): void;
  }
);

const UserProvider = ({ children }: any) => {
  const [userData, setUserData] = useState({} as IUser);
  const [businessData, setBusinessData] = useState({} as IBusiness);
  const [shiftInfo, setShiftInfo] = useState<IShift | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const userCollectionRef = collection(db, USERS_COLLECTION);
  const businessCollectionRef = collection(db, BUSINESS_COLLECTION);

  const auth = getAuth();

  const updateShift = async (
    action: string,
    updatedData?: { totalAmount: number; totalOrders: number }
  ) => {
    setLoading(true);
    if (action === "updateReport") {
      setShiftInfo({ ...shiftInfo, ...updatedData });
      setLoading(false);
    } else {
      const todayDate = getArgentinaFormat();
      const shiftCollectionRef = collection(
        db,
        BUSINESS_COLLECTION,
        businessData.uid,
        SHIFT_COLLECTION
      );
      let updatedShift = shiftInfo;

      switch (action) {
        case "open":
          updatedShift = {
            startDate: new Date(),
            status: "active",
            endDate: null,
          };
          localStorage.setItem("activeShiftID", todayDate);
          break;
        case "close":
          updatedShift = {
            endDate: new Date(),
            status: "inactive",
          };
          break;
        default:
          updatedShift = shiftInfo;
          break;
      }
      const id = localStorage.getItem("activeShiftID") || todayDate;
      const shiftDocRef = doc(shiftCollectionRef, id);
      await setDoc(shiftDocRef, updatedShift as any, { merge: true })
        .then(() => {
          if (action === "open") {
            setShiftInfo({
              id: todayDate,
              ...updatedShift,
            });
          } else setShiftInfo(null);
        })
        .finally(() => {
          setLoading(false);
          if (action === "close") localStorage.removeItem("activeShiftID");
        });
    }
  };

  const getTodayShift = async (businessID: string, userInfo: IUser) => {
    const dateString = getArgentinaFormat();
    const shiftCollectionRef = collection(
      db,
      BUSINESS_COLLECTION,
      businessID,
      SHIFT_COLLECTION
    );
    const id = localStorage.getItem("activeShiftID") || dateString;
    const shiftDocRef = doc(shiftCollectionRef, id);
    // Real-time listener for shift document
    onSnapshot(shiftDocRef, (shiftDoc) => {
      if (shiftDoc.exists()) {
        setShiftInfo({ ...shiftDoc.data(), id: shiftDoc.id } as IShift);
        localStorage.setItem("activeShiftID", id);
      } else {
        setShiftInfo(null);
      }
      setLoading(false);
    });
  };

  const handleSetBusiness = async (userInfo: IUser) => {
    const docRef = doc(businessCollectionRef, userInfo.business);
    const businessDoc = await getDoc(docRef);
    if (businessDoc.exists()) {
      setBusinessData({
        ...businessDoc.data(),
        uid: businessDoc.id,
      } as IBusiness);
      if (localStorage.getItem("activeShiftID") || userInfo.role === "KITCHEN")
        getTodayShift(businessDoc.id, userInfo);
    }
  };

  const handleSetUser = async (id: string) => {
    const docRef = doc(userCollectionRef, id);
    const userDoc = await getDoc(docRef);
    if (userDoc.exists()) {
      setUserData({ ...userDoc.data(), uid: userDoc.id } as IUser);
      handleSetBusiness(userDoc.data() as IUser);
    }
  };

  const handleSignOut = () => {
    signOut(auth)
      .then(() => {
        // El cierre de sesión fue exitoso.
        console.log("El usuario ha cerrado sesión.");
        // Puedes redirigir al usuario a la página de inicio o login
        window.location.href = "/login";
      })
      .catch((error) => {
        // Ocurrió un error.
        console.error("Error al cerrar sesión:", error);
      });
  };

  useEffect(() => {
    // Este observer escucha los cambios en el estado de autenticación
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setIsAuthenticated(!!currentUser);
      if (currentUser) {
        handleSetUser(currentUser.uid);
      }
      setLoading(false);
    });

    // La función de retorno se ejecuta cuando el componente se desmonta
    return () => unsubscribe();
  }, []);

  return (
    <UserContext.Provider
      value={{
        userData,
        businessData,
        loading,
        isAuthenticated,
        shiftInfo,
        handleSetUser,
        handleSignOut,
        setLoading,
        updateShift,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserProvider;
